import React, { forwardRef } from 'react';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import HeroParticles from '../ui/HeroParticles';
import CountdownDisplay from './CountdownDisplay';

interface MasterClassHeroProps {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  fechaLabel: string;
  horaLabel: string;
}

const TRUST_ITEMS = [
  { value: '4', label: 'pilares del Método WITS' },
  { value: 'Gratis', label: 'acceso 100% liberado' },
  { value: 'En vivo', label: 'clase interactiva' },
  { value: '∞', label: 'sin límites para aprender' },
];

const MasterClassHero = forwardRef<HTMLElement, MasterClassHeroProps>(
  ({ days, hours, minutes, seconds, fechaLabel, horaLabel }, ref) => {
    return (
      <section id="inicio" ref={ref} className="relative scroll-mt-36 overflow-hidden pb-20 pt-16 md:pb-24 md:pt-20">
        <HeroParticles />
        <div
          className="pointer-events-none absolute -left-24 -top-32 h-[28rem] w-[28rem] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(218,35,157,.4), transparent 65%)' }}
        />
        <div
          className="pointer-events-none absolute -right-20 top-0 h-[30rem] w-[30rem] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(63,134,255,.36), transparent 65%)' }}
        />

        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:gap-8">
          <div className="text-center lg:text-left">
            <span className="chip-mc mb-7 inline-flex items-center gap-2.5 rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-mc-fuchsia opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-mc-fuchsia" />
              </span>
              Masterclass gratuita · En vivo
            </span>

            <h1 className="font-display text-[2.5rem] font-extrabold leading-[1.03] tracking-tight text-white sm:text-5xl md:text-6xl">
              De usar ChatGPT
              <br className="hidden sm:block" /> a trabajar con{' '}
              <span className="text-gradient-mc">Inteligencia Artificial</span>
            </h1>

            <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-white/80 md:text-xl lg:mx-0">
              Aplica IA en tu trabajo y haz en minutos tareas que antes te tomaban horas — aunque empieces desde
              cero.
            </p>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/70 lg:justify-start">
              <span className="flex items-center gap-2">
                <ArrowRight size={14} className="text-mc-fuchsia" />
                Aprende el Método WITS paso a paso
              </span>
              <span className="flex items-center gap-2">
                <ArrowRight size={14} className="text-mc-fuchsia" />
                Sin conocimientos previos
              </span>
            </div>

            <div className="mt-8 inline-flex flex-col items-center lg:items-start">
              <span className="eyebrow-mc mb-2 text-white/45">La clase comienza en</span>
              <CountdownDisplay days={days} hours={hours} minutes={minutes} seconds={seconds} />
              <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs text-white/60 lg:justify-start">
                <span className="chip-mc flex items-center gap-1.5 rounded-lg px-3 py-1.5">
                  <Calendar size={14} className="text-mc-electric" />
                  {fechaLabel}
                </span>
                <span className="chip-mc flex items-center gap-1.5 rounded-lg px-3 py-1.5">
                  <Clock size={14} className="text-mc-electric" />
                  {horaLabel} · Hora Perú
                </span>
              </div>
            </div>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row lg:items-start lg:justify-start">
              <a href="#registro" className="btn-mc-glow group inline-flex items-center gap-2 px-8 py-4">
                Reservar mi lugar gratis
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="#metodo"
                className="chip-mc rounded-2xl px-8 py-4 font-medium text-white/90 transition-colors hover:bg-white/10"
              >
                Ver el Método WITS
              </a>
            </div>

            <div className="mt-7 flex items-center justify-center gap-3 lg:justify-start">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-mc-gradient font-display text-sm font-bold text-white shadow-lg">
                MA
              </span>
              <p className="text-left text-sm leading-snug text-white/65">
                Dictado por <span className="font-semibold text-white">Mirna Ampuero</span> — Ing. Mecatrónica (UNI)
                <br className="hidden sm:block" />
                <span className="text-xs text-white/45">Acceso gratuito · Cupos limitados</span>
              </p>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-lg animate-float">
            <div className="absolute -inset-6 animate-[spin_16s_linear_infinite] rounded-full bg-mc-gradient opacity-35 blur-3xl" />
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0a1030]/90 shadow-2xl shadow-black/50 backdrop-blur-md">
              <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
                <span className="h-3 w-3 rounded-full" style={{ background: '#ff5f57' }} />
                <span className="h-3 w-3 rounded-full" style={{ background: '#febc2e' }} />
                <span className="h-3 w-3 rounded-full" style={{ background: '#28c840' }} />
                <div className="ml-3 flex-1 text-center">
                  <span className="chip-mc mx-auto inline-block rounded-md px-3 py-1 text-[11px] text-white/50">
                    infinitywits.ai · Método WITS
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-[64px_1fr]">
                <div className="flex flex-col items-center gap-3 border-r border-white/10 bg-white/[.02] py-4">
                  {['W', 'I', 'T', 'S'].map((letter, i) => (
                    <div
                      key={letter}
                      className={`grid h-9 w-9 place-items-center rounded-lg font-display text-sm font-bold ${
                        i === 0 ? 'bg-mc-gradient text-white' : 'chip-mc text-white/70'
                      }`}
                    >
                      {letter}
                    </div>
                  ))}
                </div>
                <div className="space-y-3 p-4 text-sm">
                  <div className="flex justify-end">
                    <div className="chip-mc max-w-[80%] rounded-2xl rounded-br-sm px-3.5 py-2 text-white/80">
                      Cada día pierdo 3 horas en tareas repetitivas 😩
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-mc-gradient text-white">
                      ✦
                    </div>
                    <div
                      className="max-w-[85%] rounded-2xl rounded-bl-sm border border-white/10 px-3.5 py-3"
                      style={{
                        background: 'linear-gradient(135deg, rgba(218,35,157,.16), rgba(63,134,255,.16))',
                      }}
                    >
                      <p className="text-white/85">
                        Detecté <b>3 procesos</b> automatizables. Plan sugerido:
                      </p>
                      <div className="mt-2.5 flex flex-wrap gap-1.5 text-[11px]">
                        {['① Identifica', '② Aplica', '③ Elige', '④ Sistematiza'].map((step) => (
                          <span key={step} className="chip-mc rounded-full px-2.5 py-1">
                            {step}
                          </span>
                        ))}
                      </div>
                      <div className="mt-3">
                        <div className="mb-1 flex justify-between text-[11px] text-white/60">
                          <span>Tiempo recuperado</span>
                          <span className="font-semibold text-white">12 h / semana</span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
                          <div className="h-full rounded-full bg-mc-gradient" style={{ width: '82%' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 -left-4 flex items-center gap-2 rounded-xl border border-white/10 bg-[#0a1030]/90 px-3.5 py-2.5 shadow-xl backdrop-blur-md">
              <Clock size={20} className="text-mc-electric" />
              <div className="leading-tight">
                <div className="font-display text-sm font-bold">-12 h</div>
                <div className="text-[10px] text-white/50">a la semana</div>
              </div>
            </div>
            <div className="absolute -right-3 top-6 flex items-center gap-2 rounded-xl border border-white/10 bg-[#0a1030]/90 px-3.5 py-2.5 shadow-xl backdrop-blur-md">
              <span className="h-2 w-2 animate-pulse rounded-full bg-mc-fuchsia" />
              <div className="leading-tight">
                <div className="font-display text-sm font-bold">En vivo</div>
                <div className="text-[10px] text-white/50">100% online</div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 mx-auto mt-16 max-w-7xl px-5 md:mt-20">
          <div className="chip-mc grid grid-cols-2 divide-x divide-white/10 rounded-2xl text-center md:grid-cols-4">
            {TRUST_ITEMS.map((item) => (
              <div key={item.label} className="px-3 py-5">
                <div className="font-display text-2xl font-extrabold text-gradient-mc md:text-3xl">{item.value}</div>
                <div className="mt-1 text-xs text-white/55">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
);

MasterClassHero.displayName = 'MasterClassHero';

export default MasterClassHero;
